import { system } from '@workday/canvas-tokens-web'
import { Stack, Box, Text, Button, Flex, Alert } from '~/design_system'
import { Msg } from '~/common/localizationUtils'
import { $thm } from '~/common/themeSx'

// Rebrand: cap the SplitList card like ListLayout's BulkActionBar caps its list card.
const attachedToSplitListSx = $thm({
  '&:has(+ [data-split-list])': {
    mb: { $canvas: 0 },
    border: { $canvas: `1px solid var(${system.color.border.strong})` },
    borderBottom: { $canvas: `1px solid var(${system.color.border.default})` },
    borderTopRadius: { $canvas: 'xxl' },
    borderBottomRadius: { $canvas: 0 }
  }
})

const SelectionText = ({ itemCount, entityNameSingular, entityNamePlural, ...rest }) => (
  <Text fontSize="md" {...rest}>
    <Msg
      id="bulk_actions_banner.number_selected_text"
      count={itemCount}
      term={itemCount > 1 ? entityNamePlural : entityNameSingular}
      description="# Transactions is/are selected."
    />
  </Text>
)

/**
 * @typedef {import('~/components/v2/AlertBanner').AlertBannerProps} AlertBannerProps
 *
 * @param {{
 *  entityNameSingular: string,
 *  entityNamePlural: string,
 *  selectedAllItemsOnAllPages?: boolean,
 *  selectedAllItemsOnCurrentPage?: boolean,
 *  selectAllItemsOnAllPages?: (e) => void,
 *  selectedItemsCount: number,
 *  selectedItemsMaxCount?: number,
 *  children: React.ReactNode | React.ReactNode[],
 *  showSelectAll?: boolean,
 * } & AlertBannerProps} props
 */
export const BulkActionsBanner = ({
  entityNameSingular, //                        entity name in singular (e.g. Invoice, Expense,...)
  entityNamePlural, //                          entity name in plurar (e.g. Invoices, Expenses,...)
  selectedAllItemsOnAllPages = undefined, //    are all items selected in the entiry queryset
  selectedAllItemsOnCurrentPage = undefined, // are all items selected on the current page
  selectAllItemsOnAllPages = undefined, //      callback to select all items in the entiry set
  selectedItemsCount, //                        number of selected items
  selectedItemsMaxCount = undefined, //         maximum number of items that can be selected
  children, //                     bulk action children components
  showSelectAll = true, //                      show select all link
  sx = undefined,
  ...rest
}) => {
  const itemCount = selectedAllItemsOnAllPages ? selectedItemsMaxCount : selectedItemsCount
  return (
    <Alert
      data-bulk-actions-banner
      variant="actionBar"
      aria-live="polite"
      sx={{ ...attachedToSplitListSx, ...sx }}
      {...rest}>
      <Flex width="100%" justifyContent="space-between" alignItems="center">
        <Stack direction={{ base: 'column', sm: 'row' }}>
          {itemCount > 0 && (
            <SelectionText
              itemCount={itemCount}
              entityNameSingular={entityNameSingular}
              entityNamePlural={entityNamePlural}
            />
          )}

          {!selectedAllItemsOnAllPages && selectedAllItemsOnCurrentPage && showSelectAll && (
            <Box>
              <Button
                variant="inlineLink"
                color="inherit"
                data-testid="select_all_from_all_pages"
                _hover={{ textDecoration: 'none' }}
                pl={4}
                onClick={selectAllItemsOnAllPages}>
                {selectedItemsMaxCount > 1
                  ? `Select All ${selectedItemsMaxCount} ${entityNamePlural}`
                  : `Select 1 ${entityNameSingular}`}
              </Button>
            </Box>
          )}
        </Stack>
        <Stack
          w={{ base: 'full', sm: 'initial' }}
          justifyContent="flex-end"
          alignItems="flex-end"
          direction={{ base: 'column', sm: 'row' }}>
          {children}
        </Stack>
      </Flex>
    </Alert>
  )
}
